import { InputHTMLAttributes } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import clsx from 'clsx'

import TextField, { TextFieldProps } from 'shared/ui/form/TextField'
import { isString } from 'shared/utils/common'

import { TFormRules } from './types'

import styles from './ControlledTextField.module.scss'

interface ControlledTextFieldProps {
  type?: InputHTMLAttributes<HTMLInputElement>['type']
  inputProps?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>
  startIcon?: React.ReactElement | string | null
  endIcon?: React.ReactElement | string | null
  label: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  name: string
  rules: TFormRules
  noMargin?: boolean
  size?: 'medium' | 'large'
  id?: string
}

export function ControlledTextField({
  inputProps,
  type = 'text',
  disabled = false,
  required = false,
  startIcon,
  endIcon,
  name,
  rules,
  placeholder,
  label,
  noMargin = false,
  size,
  id
}: ControlledTextFieldProps) {
  const {
    control,
    formState: { errors }
  } = useFormContext()

  const errorMessage = errors?.[name]?.message || ''
  const hasError = isString(errorMessage) && errorMessage !== ''

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => {
        return (
          <StyledTextField
            id={id}
            {...field}
            disabled={disabled}
            type={type || 'text'}
            error={hasError}
            inputProps={inputProps}
            helperText={hasError ? <>{errorMessage as string}</> : undefined}
            placeholder={placeholder}
            size={size}
            $noMargin={noMargin}
            label={label}
            required={required}
            icons={{
              start: startIcon ? <>{startIcon}</> : null,
              end: endIcon ? <>{endIcon}</> : null
            }}
            textHelperProps={{ 'aria-live': 'polite' }}
          />
        )
      }}
    />
  )
}

const StyledTextField = ({
  $noMargin,
  className,
  ...props
}: TextFieldProps & { $noMargin?: boolean }) => (
  <TextField
    {...props}
    className={clsx(
      styles.textField,
      { [styles.noMargin]: $noMargin },
      className
    )}
  />
)
